import { useSelector } from 'react-redux';
import { Txt } from '@vst/beam';
import { useTranslation } from 'react-i18next';

import { selectAddOns, selectChangePlan } from '@app/redux';

import { GetCurrentAddOnsPayload } from '@mfe/shared/schema-types';

import { CardContent } from './CardContent';
import DroppedAddonIcon from './DroppedAddonIcon';
import DroppedAddons from './DroppedAddonsIcons';
import { AddonProductTypeIds } from '../../types';
import { ADDON_KIND } from '../../../../constants';

import styles from './OptionalServiceWarning.module.scss';

type OptionalServiceWarningProps = {
  handleBackNavigation: () => void;
};

const OptionalServiceWarning = ({ handleBackNavigation }: OptionalServiceWarningProps) => {
  const { currentAddOns } = useSelector(selectAddOns);

  const hasStaticIP = currentAddOns?.find((addOn: GetCurrentAddOnsPayload) => addOn.kind === ADDON_KIND.STATIC_IP);

  const handleBack = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    handleBackNavigation();
  };

  return hasStaticIP ? (
    <StaticIpAddressChangeWarning handleBack={handleBack} />
  ) : (
    <DroppedAddonsWarning handleBack={handleBack} />
  );
};

export default OptionalServiceWarning;

const StaticIpAddressChangeWarning = ({ handleBack }: { handleBack: (e: React.MouseEvent<HTMLElement>) => void }) => {
  const { t } = useTranslation('ChangePlan', { keyPrefix: 'droppedAddOns' });

  return (
    <CardContent
      handleBack={handleBack}
      serviceWarning={t('cardInfo_StaticIP')}
      services={
        <div key={AddonProductTypeIds.STATIC_IP} data-cy="StaticIpIconWithName" className={styles['text-icon']}>
          <DroppedAddonIcon productTypeId={AddonProductTypeIds.STATIC_IP} />
          <Txt pl={'8px'} variant={'bodyLargeBold'}>
            {t('staticIP')}
          </Txt>
        </div>
      }
      description={
        <Txt variant="bodySmallRegular" color="subtle">
          {t('body_StaticIP')}
        </Txt>
      }
      consentLabel={t('checkboxText_StaticIP')}
    />
  );
};

const DroppedAddonsWarning = ({ handleBack }: { handleBack: (e: React.MouseEvent<HTMLElement>) => void }) => {
  const { t } = useTranslation('ChangePlan', { keyPrefix: 'droppedAddOns' });

  const { currentAddOns } = useSelector(selectAddOns);
  const activeEasyCare = currentAddOns?.find(({ kind }) => kind === ADDON_KIND.EASY_CARE);

  const { droppedAddOns } = useSelector(selectChangePlan);

  const isBundledEasyCareDropping = droppedAddOns.some(
    ({ productTypeId }) => productTypeId === AddonProductTypeIds.EASY_CARE_BUNDLED
  );

  // User has active EasyCare paid on top of Unleashed Max plan with bundled EasyCare
  const hasCustomEasyCareVerbiage = isBundledEasyCareDropping && Boolean(activeEasyCare) && !activeEasyCare?.isBundle;

  const getServiceWarningMessage = () => {
    if (hasCustomEasyCareVerbiage) {
      return t('cardInfo_bundledEasyCareWithPaidOption', { paidEasyCare: activeEasyCare?.name });
    }
    if (isBundledEasyCareDropping) {
      return t('cardInfo_bundledEasyCare');
    }
    return t('cardInfo');
  };

  const showDefaultContent = !(hasCustomEasyCareVerbiage || isBundledEasyCareDropping);

  const defaultDroppedAddonsDescription = (
    <>
      <Txt variant="bodySmallRegular" color="subtle">
        {t('body')}
      </Txt>
      <Txt variant="bodySmallRegular" color="subtle" mt="16px">
        {t('body2')}
      </Txt>
    </>
  );

  return (
    <CardContent
      handleBack={handleBack}
      serviceWarning={getServiceWarningMessage()}
      services={showDefaultContent ? <DroppedAddons droppedAddOns={droppedAddOns} /> : null}
      description={showDefaultContent ? defaultDroppedAddonsDescription : null}
      consentLabel={t('checkboxText')}
    />
  );
};
